import { S3_BUCKET_URL } from 'src/constants';

const DEFAULT_REPORT_LOGO_URL = 'https://grams-images-bucket.s3.us-west-2.amazonaws.com/grams/grams_logo.jpg';

type MaybeString = string | null | undefined;

type LocalReportOrganization = {
  orgId?: MaybeString;
  name?: MaybeString;
  logo_key?: MaybeString;
} | null | undefined;

type LocalReportCenter = {
  centerId?: MaybeString;
  name?: MaybeString;
  addressDistrict?: MaybeString;
  addressCity?: MaybeString;
} | null | undefined;

type LocalReportSurveyor = {
  surveyorId?: MaybeString;
  name?: MaybeString;
  phoneNumber?: MaybeString;
} | null | undefined;

type LocalReportUserSettings = {
  logo?: MaybeString;
} | null | undefined;

export type LocalReportContext = {
  info: Record<string, string>;
  organizationName?: string;
  centerName?: string;
  organizationLogoKey?: string;
  organizationLogoUrl?: string;
  reportLogoUrl?: string;
};

type BuildLocalReportContextArgs = {
  baseInfo?: Record<string, unknown> | null;
  location?: MaybeString;
  organization?: LocalReportOrganization;
  center?: LocalReportCenter;
  surveyor?: LocalReportSurveyor;
  userSettings?: LocalReportUserSettings;
};

const normalizeString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const setInfoValue = (info: Record<string, string>, key: string, value: unknown) => {
  const normalized = normalizeString(value);
  if (normalized) {
    info[key] = normalized;
  }
};

const buildS3ObjectUrl = (objectKey: MaybeString): string | undefined => {
  const normalizedKey = normalizeString(objectKey);
  if (!normalizedKey) return undefined;
  if (/^https?:\/\//i.test(normalizedKey)) return normalizedKey;

  const bucketUrl = normalizeString(S3_BUCKET_URL());
  if (!bucketUrl) return undefined;

  return `${bucketUrl.replace(/\/$/, '')}/${normalizedKey.replace(/^\//, '')}`;
};

export const buildLocalReportContext = ({
  baseInfo,
  location,
  organization,
  center,
  surveyor,
  userSettings,
}: BuildLocalReportContextArgs): LocalReportContext => {
  const info: Record<string, string> = {};

  if (baseInfo && typeof baseInfo === 'object') {
    Object.entries(baseInfo).forEach(([key, value]) => {
      setInfoValue(info, key, value);
    });
  }

  setInfoValue(info, 'location', location);
  setInfoValue(info, 'orgId', organization?.orgId);
  setInfoValue(info, 'organizationName', organization?.name);
  setInfoValue(info, 'orgName', organization?.name);
  setInfoValue(info, 'centerId', center?.centerId);
  setInfoValue(info, 'centerName', center?.name);
  setInfoValue(info, 'centerDistrict', center?.addressDistrict);
  setInfoValue(info, 'centerCity', center?.addressCity);
  setInfoValue(info, 'surveyorId', surveyor?.surveyorId);
  setInfoValue(info, 'surveyorName', surveyor?.name);
  setInfoValue(info, 'surveyorPhoneNumber', surveyor?.phoneNumber);

  const organizationLogoKey = normalizeString(organization?.logo_key) || normalizeString(userSettings?.logo);
  const organizationLogoUrl = buildS3ObjectUrl(organization?.logo_key) || buildS3ObjectUrl(userSettings?.logo);
  const reportLogoUrl = buildS3ObjectUrl(userSettings?.logo) || organizationLogoUrl || DEFAULT_REPORT_LOGO_URL;

  setInfoValue(info, 'organizationLogoKey', organizationLogoKey);
  setInfoValue(info, 'orgLogoKey', organizationLogoKey);
  setInfoValue(info, 'organizationLogoUrl', organizationLogoUrl);
  setInfoValue(info, 'orgLogoUrl', organizationLogoUrl);
  setInfoValue(info, 'reportLogoUrl', reportLogoUrl);
  setInfoValue(info, 'logoUrl', reportLogoUrl);

  return {
    info,
    organizationName: normalizeString(organization?.name),
    centerName: normalizeString(center?.name),
    organizationLogoKey,
    organizationLogoUrl,
    reportLogoUrl,
  };
};

export const getCurrentLocalReportContext = (): LocalReportContext => {
  const { store } = require('../redux/store');
  const state = store.getState();
  const profile = state.user?.userProfile?.response;

  return buildLocalReportContext({
    organization: profile?.org,
    center: profile?.center,
    surveyor: profile?.surveyor,
    userSettings: profile?.settings,
  });
};

export { DEFAULT_REPORT_LOGO_URL };
